import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import React from 'react';

import { ActionPermissions, CustomPermission } from '../../../@types';
import { Link } from '../../../components/atoms/Link';
import { isUserHasPermission } from '../../../configs/permissions';
import { STANDARD_FIELD_HEIGHT } from '../../../configs/ui-constants';

export interface ShipmentListHeaderProps {
  total: number;
  permissions: CustomPermission[] | undefined;
  exportAnchor: HTMLElement | null;
  onExportClick: (event: React.MouseEvent<HTMLElement>) => void;
  onExportClose: () => void;
  onExportCsv: () => void;
  onExportExcel: () => void;
  onColumnsClick: () => void;
}

const TOOLBAR_BUTTON_SX = {
  textTransform: 'none' as const,
  fontSize: '12px',
  color: '#fff',
  borderColor: 'rgba(255, 255, 255, 0.3)',
  bgcolor: 'transparent',
  py: 0.25,
  height: STANDARD_FIELD_HEIGHT,
  minHeight: STANDARD_FIELD_HEIGHT,
  '&:hover': {
    borderColor: '#fff',
    bgcolor: 'rgba(255, 255, 255, 0.08)',
  },
} as const;

export const ShipmentListHeader: React.FC<ShipmentListHeaderProps> = ({
  total,
  permissions,
  exportAnchor,
  onExportClick,
  onExportClose,
  onExportCsv,
  onExportExcel,
  onColumnsClick,
}) => {
  return (
    <Box
      component="header"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'v4.sidebar',
        color: '#fff',
        px: '20px',
        py: '12px',
        flexWrap: 'wrap',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>Shipments</Typography>
        {total > 0 && (
          <Box
            component="span"
            sx={{
              fontSize: '11px',
              color: 'rgba(255, 255, 255, 0.85)',
              fontWeight: 400,
              bgcolor: 'rgba(255, 255, 255, 0.12)',
              px: 1,
              py: '2px',
              borderRadius: '10px',
            }}
          >
            {total.toLocaleString()} results
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        <MuiButton
          variant="outlined"
          size="small"
          startIcon={<ViewColumnOutlinedIcon sx={{ fontSize: 14 }} />}
          onClick={onColumnsClick}
          sx={TOOLBAR_BUTTON_SX}
        >
          Columns
        </MuiButton>
        {isUserHasPermission(ActionPermissions.Shipment_View_Search, permissions) && (
          <MuiButton
            variant="outlined"
            size="small"
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={onExportClick}
            sx={TOOLBAR_BUTTON_SX}
          >
            Export
          </MuiButton>
        )}
        <Menu
          anchorEl={exportAnchor}
          open={Boolean(exportAnchor)}
          onClose={onExportClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={onExportCsv}>Export CSV</MenuItem>
          <MenuItem onClick={onExportExcel}>Export Excel</MenuItem>
        </Menu>
        {isUserHasPermission(ActionPermissions.Shipment_View_Dispatch, permissions) && (
          <Link href="/shipments/dispatch/create">
            <MuiButton
              variant="outlined"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 14 }} />}
              sx={TOOLBAR_BUTTON_SX}
            >
              Create Shipment
            </MuiButton>
          </Link>
        )}
      </Box>
    </Box>
  );
};
